import ipaddr from 'ipaddr.js';
import Model from '@/common/models/common.model';
import {
  STATUS_ICON_LOADING
} from '@/common/utils/statuses';
import {
  SERVER_STATUSES,
  SERVER_STATUSES_GROUPS,
  SERVER_STATUSES_ICON_MAP,
  SERVER_STATUSES_TEXT,
  SERVER_TASK_STATES,
  TASK_STATE_TEXTS
} from '@/common/constants/servers.constants';
import { BYTES_IN_GIBIBYTES, roundNumber } from '@/common/utils/formatter.number';
import { Server as ServerSchema, DomainProject as DomainProjectSchema } from '@schema';
import { store } from '@/common/root';
import useComputeVolumesStore from '@/common/store/pinia/computeVolumes';
import { Volume } from '@/common/store/pinia/computeVolumes/computeVolumeMapper';
import { ComputeImage } from '@/common/store/pinia/computeImages/computeImageMapper';
import useComputeImagesStore from '@/common/store/pinia/computeImages';

export default class Server extends Model {
  declare attributes: ServerSchema & { network_data: NetworkInterfaceSchema[], tags: string[] };

  get statusIcon() {
    return this.isInProgress ? STATUS_ICON_LOADING : SERVER_STATUSES_ICON_MAP[this.status];
  }

  get statusText() {
    return SERVER_STATUSES_TEXT[this.status];
  }

  get resourcesViewName(): string {
    return `server-resources-${this.id}`;
  }

  get flavor() {
    return this.attributes.flavor || {};
  }

  get machineType() {
    return this.attributes.machine_type;
  }

  get volumes(): Volume[] {
    const computeVolumesStore = useComputeVolumesStore();
    return this.volumesList.map((vol) => computeVolumesStore.getItemById(vol.id))
      .filter((volume): volume is Volume => !!volume);
  }

  get image(): ComputeImage | Record<string, never> {
    const computeImagesStore = useComputeImagesStore();
    let image: ComputeImage | undefined;
    if (this.imageId) {
      image = computeImagesStore.getItemById(this.imageId);
    }
    return image || {};
  }

  get imageName() {
    // eslint-disable-next-line camelcase
    return this.bootVolume?.meta?.image_name;
  }

  get bootVolume(): Volume | undefined | null {
    if (this.bootableVolumeId && this.volumes) {
      return this.volumes.find(vol => vol.id === this.bootableVolumeId);
    }
    return null;
  }

  get project(): DomainProjectSchema {
    return (typeof store.getters['projects/getItemById'] === 'function'
      && store.getters['projects/getItemById'](this.projectId)) || {};
  }

  // STATES
  get isBuilding() {
    return SERVER_STATUSES_GROUPS.Building.indexOf(this.status) > -1;
  }

  get isDeleting() {
    return this.taskState === SERVER_TASK_STATES.DELETING;
  }

  get isDeletable(): boolean {
    return !this.isInProgress;
  }

  get isActive() {
    return SERVER_STATUSES_GROUPS.Active.indexOf(this.status) > -1;
  }

  get isRunning() {
    return SERVER_STATUSES_GROUPS.Running.indexOf(this.status) > -1;
  }

  get isInProgress() {
    return SERVER_STATUSES_GROUPS.InProgress.indexOf(this.taskState || this.status) > -1;
  }

  get isStopped() {
    return SERVER_STATUSES_GROUPS.Stopped
      .concat(SERVER_STATUSES_GROUPS.Shelved)
      .indexOf(this.status) > -1;
  }

  get isInRescue() {
    return this.status === SERVER_STATUSES.RESCUE;
  }

  get isShelved() {
    return SERVER_STATUSES_GROUPS.Shelved.indexOf(this.status) > -1;
  }

  get isErrored() {
    return SERVER_STATUSES_GROUPS.Errored.indexOf(this.status) > -1;
  }

  get isShutoff() {
    return this.status === SERVER_STATUSES.SHUTOFF;
  }

  get isPaused() {
    return this.status === SERVER_STATUSES.PAUSED;
  }

  get isSuspended() {
    return this.status === SERVER_STATUSES.SUSPENDED;
  }

  get isMigrating() {
    return SERVER_STATUSES_GROUPS.IsMigrating.indexOf(this.taskState || this.status) > -1;
  }

  get isResizing() {
    return SERVER_STATUSES_GROUPS.IsResizing.indexOf(this.taskState || this.status) > -1;
  }

  get isRebootingSoft() {
    return SERVER_STATUSES_GROUPS.isRebootingSoft.indexOf(this.taskState) > -1;
  }

  get isDiskResizable() {
    return [SERVER_STATUSES.ACTIVE, SERVER_STATUSES.PAUSED, SERVER_STATUSES.SHUTOFF]
      .indexOf(this.status) > -1;
  }

  get ha() {
    return !!this.attributes.ha_enabled;
  }

  get status() {
    return this.attributes.status;
  }

  get taskState() {
    return this.attributes.task_state;
  }

  get hostStatus() {
    // if host is up - host_status UP
    // if shutdown - host_status UNKNOWN
    // if node in maintenance - host_status MAINTENANCE
    // if fenced - host_status DOWN
    return this.attributes.host_status;
  }

  get taskStateText() {
    return this.taskState && TASK_STATE_TEXTS[this.taskState];
  }

  get ip() {
    return this.ips?.[0] || '';
  }

  get ips(): string[] {
    const { networks, attributes: { network_data: networkData } } = this;
    // this.networkData contain up-to-date information about fixed_ips assigned to the VM
    // but it required one more extra request to fetch the interfaces
    // Here we pick the most relevant networks source to display:
    let fixedIpsSource = (networks instanceof Array ? networks : []) as any[];
    let fixedIpsProp = 'ips';
    if (networkData) {
      fixedIpsSource = networkData;
      fixedIpsProp = 'fixed_ips';
    }

    return fixedIpsSource.reduce((result, net) => net?.[fixedIpsProp]
      ? result.concat(net[fixedIpsProp])
      : result, []);
  }

  get hostName(): string {
    return this.attributes.host;
  }

  /* Name of the node which server is attached */
  get node(): string {
    return this.hostName && this.attributes.orig_hostname;
  }

  /* Networks in Nova order */
  get networks() {
    return this.attributes.networks?.map((net, index) => ({
      ...net,
      novaIndex: index
    }));
  }

  /* Network interfaces with mac address used as unique id */
  get networkData(): ServerNetworkData[] {
    if (!this.attributes.network_data) {
      return [];
    }
    const indexes = this.networks?.reduce((obj, item) => {
      obj[item.mac_addr] = { novaIndex: item.novaIndex, name: item.name, ipam_enabled: item.ipam_enabled };
      return obj;
    }, {}) || {};

    const groupByKind = (itemsArr, ipVersion) => itemsArr.reduce((acc, ip) => ip.kind() === `ipv${ipVersion}`
      ? acc.concat({ ip, ipVersion })
      : acc, []);

    const networkItems = this.attributes.network_data.map((ifs) => {
      const ifsByNovaIndex = indexes[ifs.mac_addr];
      const fixedIps = ifsByNovaIndex?.ipam_enabled ? ifs.fixed_ips : undefined;
      const ipsParsed = (fixedIps || []).map(ip => ipaddr.parse(ip));
      const ipv6 = groupByKind(ipsParsed, 6);
      const ipv4 = groupByKind(ipsParsed, 4);

      return {
        ...ifs,
        ...ifsByNovaIndex,
        fixed_ips: fixedIps,
        primaryIps: [ipv4[0], ipv6[0]].filter(Boolean),
        secondaryIps: [...ipv4.slice(1), ...ipv6.slice(1)].filter(Boolean),
      };
    }) || [];

    return networkItems.sort((a, b) => a.novaIndex > b.novaIndex ? 1 : -1);
  }

  getIpsByVersion(networkId, ipVersion): string[] {
    return this.networks.filter(it => it.id === networkId)
      .flatMap(net => net.ips.filter(ip => ipaddr.parse(ip)?.kind() === `ipv${ipVersion}`));
  }

  get createdAt() {
    return this.attributes.created;
  }

  get updatedAt() {
    return this.attributes.updated;
  }

  get sshKey() {
    return this.attributes.key_name;
  }

  get username() {
    return this.attributes.username;
  }

  get imageId(): ID | undefined {
    return this.bootVolume?.meta?.image_id;
  }

  get osType() {
    return this.bootVolume?.meta?.os_type;
  }

  get flavorName() {
    return this.flavor.original_name;
  }

  get vcpu() {
    return this.flavor.vcpus;
  }

  get vcpusUsed() {
    return this.attributes.statistics?.metrics?.cpu_usage || 0;
  }

  // Number or vcpu usage integer
  get vcpusUsedInt(): number {
    return roundNumber(this.vcpusUsed / 100);
  }

  // ram is in MiB
  get ram() {
    return this.flavor.ram;
  }

  get ramUsed() {
    return this.attributes.statistics?.metrics?.mem_usage || 0;
  }

  get storage() {
    return this.volumes.reduce((accumulator, volume) => accumulator + volume.size, 0);
  }

  get storageCapacity() {
    const capacity = this.attributes.statistics?.metrics?.block_capacity;
    if (capacity) {
      return capacity;
    }
    return this.volumesList.reduce((accumulator, volume) => accumulator + (volume.size || 0), 0) * BYTES_IN_GIBIBYTES;
  }

  get storageUsage() {
    return this.attributes.statistics?.metrics?.block_usage || 0;
  }

  get volumeIds(): ID[] {
    return this.volumesList.map(it => it.id);
  }

  get volumesList() {
    return this.attributes.volumes || [];
  }

  get volumesCount() {
    return this.volumesList.length;
  }

  get autoDeletedVolumesCount() {
    return this.volumesList.filter(it => it.delete_on_termination).length || 0;
  }

  get bootableVolumeId(): ID | null {
    const volumes = this.volumesList;
    return volumes.length ? volumes[0].id : null;
  }

  get errorData() {
    return this.attributes.fault || null;
  }

  get errorMessage() {
    return this.errorData?.message || '';
  }

  get projectId(): ID {
    return this.attributes.project_id;
  }

  get domainId(): ID {
    return this.attributes.domain_id;
  }

  get projectName() {
    return this.project.name;
  }

  get traits(): ID[] {
    return this.attributes.traits || [];
  }

  get tags(): string[] {
    return this.attributes.tags?.slice().sort() || [];
  }

  get maxVcpus() {
    return this.attributes.max_vcpu;
  }

  get maxRam() {
    return this.attributes.max_mem;
  }

  get isLiveResizeEnabled() {
    return this.allowLiveResize;
  }

  get hasPCIdevices() {
    return this.attributes.has_pci_devices;
  }

  get hasVGPU() {
    return Boolean(
      parseInt(this.flavor.extra_specs?.['resources:VGPU'] || '', 10)
    );
  }

  get liveMigrationUnavailable() {
    return this.hasPCIdevices || this.hasVGPU;
  }

  get isLiveResizeAllowed() {
    return this.isLiveResizeEnabled && this.isActive;
  }

  get metadata(): any {
    return this.attributes.metadata;
  }

  get allowLiveResize() {
    return !!this.attributes.allow_live_resize;
  }

  get allowVMPassword() {
    return this.attributes.allow_set_password;
  }

  get bootParams() {
    return {
      vtpm: this.volumes[0]?.vtpm,
      secureBoot: this.volumes[0]?.secureBoot,
      uefi: this.volumes[0]?.uefi
    };
  }
}
